import type { PasswordComplexityRule } from "@auth0/auth0-acul-react/types";
import { Check } from "lucide-react";

import { cn } from "@/lib/utils";

export interface ULThemePasswordValidatorProps {
  /**
   * Array of password validation rules from usePasswordValidation hook
   */
  validationRules: PasswordComplexityRule[];
  /**
   * Optional class names for additional styling
   */
  className?: string;
  /**
   * Whether to show the validation box (only show when password has content)
   */
  show?: boolean;
  passwordSecurityText?: string;
  /**
   * Thon+ (hotels org) uses black text/check for satisfied rules instead of
   * the default success-green.
   */
  isHotelsOrg?: boolean;
  /**
   * Screen-reader-only status words appended to each requirement, since the
   * checkmark icon and color are otherwise the only way "met" is conveyed.
   */
  metStatusText?: string;
  unmetStatusText?: string;
}

export const ULThemePasswordValidator = ({
  validationRules,
  className,
  passwordSecurityText,
  show = true,
  isHotelsOrg = false,
  metStatusText = "met",
  unmetStatusText = "not met",
}: ULThemePasswordValidatorProps) => {
  if (!show || !validationRules || validationRules.length === 0) {
    return null;
  }

  const validColor = isHotelsOrg ? "text-body-text" : "text-success";
  const itemTextClasses = "text-(length:--ul-theme-font-body-text-size) font-body";

  const renderValidationItem = (rule: PasswordComplexityRule) => {
    const hasNestedItems = rule.items && rule.items.length > 0;

    return (
      <li
        key={rule.code}
        className={cn(
          itemTextClasses,
          "relative",
          rule.isValid ? cn("list-none", validColor) : "text-body-text",
        )}
      >
        {rule.isValid && (
          <Check
            className={cn("absolute -left-5 top-0.5 h-4 w-4", validColor)}
            data-testid={`check-icon-${rule.code}`}
            aria-hidden="true"
          />
        )}
        <div>
          <span>
            {rule.label}
            <span className="sr-only">, {rule.isValid ? metStatusText : unmetStatusText}</span>
          </span>
          {/* Render nested items if they exist */}
          {hasNestedItems && (
            <ul className="mt-1 space-y-1 pl-4 list-disc">
              {rule.items!.map((item) => (
                <li
                  key={item.code}
                  className={cn(itemTextClasses, item.isValid ? validColor : "text-body-text")}
                >
                  {item.label}
                  <span className="sr-only">
                    , {item.isValid ? metStatusText : unmetStatusText}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </li>
    );
  };

  return (
    <div className={cn("bg-widget-bg border border-input rounded-input p-4 mb-4", className)}>
      <div className="text-(length:--ul-theme-font-body-text-size) text-body-text mb-3">
        {passwordSecurityText}
      </div>

      <ul className="space-y-2 pl-4 list-disc" aria-live="polite">
        {validationRules.map((rule) => renderValidationItem(rule))}
      </ul>
    </div>
  );
};

ULThemePasswordValidator.displayName = "ULThemePasswordValidator";
